/**
 * Magic-link redirect handler. Supabase's PKCE flow exchanges the code in
 * the URL for a session via `supabase.auth.exchangeCodeForSession`, but
 * with `detectSessionInUrl: true` (set in lib/supabase.ts), the client
 * does this automatically on page load. By the time this component renders,
 * the session is already established and onAuthStateChange has fired.
 *
 * We just need to wait one tick for the AuthProvider to pick it up, then
 * redirect to `?next=` (default `/app`).
 */
import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { useAuth } from '@/hooks/useAuth';

const searchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute('/auth/callback')({
  validateSearch: searchSchema,
  component: AuthCallback,
});

function AuthCallback() {
  const { next } = Route.useSearch();
  const { session, initializing } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (initializing) return;
    if (session) {
      navigate({ to: next ?? '/app', replace: true });
    } else {
      // No session despite landing here — magic link may have expired
      // or the auth flow failed silently. Send the user back to sign-in
      // with a hint.
      navigate({
        to: '/sign-in',
        search: { redirect: next },
        replace: true,
      });
    }
  }, [initializing, session, next, navigate]);

  return (
    <AuthLayout title="Signing you in…">
      <p className="text-sm leading-relaxed text-charcoal">
        One moment — completing your sign-in.
      </p>
    </AuthLayout>
  );
}
